<template>
    <div class="filter-group">
        <div class="control-buttons" v-if="showRemoveButton">
            <div class="control-button">
                <md-button class="md-icon-button md-mini" @click="() => onRemoved(clone.id)">
                    <md-icon>delete_outline</md-icon>
                </md-button>
            </div>
        </div>

        <SingleFilter
            v-for="(filter, index) of clone.filters"
            v-model="clone.filters[index]"
            :enable-operator-changed="clone.filters.length > 1"
            :operator="clone.operator"
            :on-operator-changed="onOperatorChanged"
            :selected="filter.id === selectedFilterId"
            :on-selected="() => (selectedFilterId = filter.id)"
            :on-canceled="() => (selectedFilterId = null)"
            :on-removed="onFilterRemoved"
            :key="filter.id"
            :group-id="value.id"
            :on-group-select-hovered="(hovered) => (groupSelectHovered = hovered)"
            :group-select-hovered="groupSelectHovered"
        />
        <div class="filter-row" :class="{ empty: !clone.filters.length }">
            <a-popover
                v-model="attributePickerVisible"
                trigger="click"
                placement="left"
                :overlayClassName="'no-padding'"
            >
                <div slot="content">
                    <AttributePicker :default-selected="null" :pick="onAttributePicked" :filter-mode="true" />
                </div>
                <div class="add-filter-button">
                    <span class="material-icons"> add </span>
                    {{ $t("chatbotv2.addNewFilter") }}
                </div>
            </a-popover>
        </div>
    </div>
</template>

<script>
import log2 from "../../../../../../../utils/log2";
import { generateRandomId } from "../../../../../../../utils/session";
import SingleFilter from "./SingleFilter";
import AttributePicker from "../../pickers/AttributePicker";
import _ from "lodash";
import CONFIG from "../../../../../../../config/config";
import systemAttributes from "../../../../utils/systemAttributes";
import { mapState } from "vuex";

const log = log2("FilterGroup");
export default {
    name: "FilterGroup",
    components: {
        SingleFilter,
        AttributePicker,
    },
    props: {
        value: {
            type: Object,
            required: true,
            default: () => ({
                id: generateRandomId(),
                operator: "or",
                filters: [
                    {
                        id: generateRandomId(),
                        rule: "greater_or_equal",
                        attribute: "email",
                        value: 0,
                    },
                ],
            }),
        },
        onRemoved: {
            type: Function,
            required: true,
        },
        showRemoveButton: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            selectedFilterId: null,
            attributePickerVisible: false,
            clone: _.cloneDeep(this.value),
            groupSelectHovered: false,
        };
    },
    computed: {
        ...mapState({
            visitorContactAttributes: (state) => state.contactProperty.data || [],
        }),
    },
    methods: {
        onOperatorChanged(operator) {
            log("onOperatorChanged", operator);
            this.clone.operator = operator;
        },
        onAttributePicked(attribute) {
            const contactProperties = this.visitorContactAttributes.map((x) => ({
                name: x.label,
                placeholder: "",
                value: "contact_property_" + x._id,
                type: "visitorContactProperty",
                availableOperators: ["equal", "not_equal", "empty", "non_empty"],
                defaultFilterValue: "",
            }));
            const id = generateRandomId();
            let value = 0;
            const foundAttr =
                systemAttributes.find((x) => x.value === attribute) ||
                CONFIG.AKINON.attributes.find((x) => x.value === attribute) ||
                CONFIG.IDEASOFT.attributes.find((x) => x.value === attribute) ||
                CONFIG.PROBEL.attributes.find((x) => x.value === attribute) ||
                contactProperties.find((x) => x.value === attribute);
            let rule = _.get(foundAttr, "defaultOperator", "equal");
            value = _.get(foundAttr, "defaultFilterValue", value);
            if (attribute === "akinon_order_status") {
                value = CONFIG.AKINON.ORDER_STATUSES.DELIVERED;
                rule = "equal";
            }
            this.clone.filters.push({
                id,
                rule,
                attribute,
                value,
            });
            this.attributePickerVisible = false;
            this.selectedFilterId = id;
        },
        onFilterRemoved(id) {
            log("onFilterRemoved", id);
            this.clone.filters = this.clone.filters.filter((filter) => filter.id !== id);
            if (this.selectedFilterId === id) {
                this.selectedFilterId = null;
            }
        },
    },
    mounted() {
        log("mounted");
    },
    watch: {
        clone: {
            handler(newValue) {
                this.$emit("input", newValue);
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss">
.filter-group {
    grid-row-gap: 15px;
    background: #fff;
    margin: 0 10px;
    padding: 10px;
    border-radius: 4px;
    display: grid;
    position: relative;

    &:hover {
        .control-buttons {
            opacity: 1;
        }
    }

    .control-buttons {
        height: 100%;
        left: -44px;
        opacity: 0;
        position: absolute;
        top: 0;
        transition: opacity 0.3s;
        width: 44px;

        .md-button {
            background-color: #fff;
            align-items: center;
            border-radius: 100%;
            box-shadow: 0 1px 4px 0 rgb(0 0 0 / 19%);
            cursor: pointer;
            display: flex;
            justify-content: center;
            margin-bottom: 7px;
            transition: background-color 0.3s;
            height: 30px;
            width: 30px;
            min-height: 30px !important;
            min-width: 30px !important;

            .md-icon {
                color: #006cff;
            }
        }
    }

    .filter-row {
        grid-column-gap: 10px;
        display: grid;
        grid-template-columns: minmax(0, 1fr) 10px;
        width: 100%;

        .add-filter-button {
            align-items: center;
            border: 1px solid #c6cdd4;
            border-radius: 4px;
            color: #006cff;
            cursor: pointer;
            display: flex;
            font-weight: 600;
            padding: 8px 15px;
            transition: border 0.3s, opacity 0.3s;

            &:hover {
                border-color: #006cff;
            }
        }

        &.empty {
            grid-template-columns: minmax(0, 1fr);
        }
    }
}
</style>
